<template>
  <Dialog 
    v-model:visible="visible" 
    header="UNIBO Exam Simulation Timer" 
    :modal="true" 
    class="w-full max-w-md bg-slate-900 border border-slate-800 text-slate-100"
  >
    <div class="space-y-5 p-2 select-none">
      <!-- Big Digital Display -->
      <div class="bg-slate-950 p-6 rounded-2xl border border-slate-800 text-center shadow-inner">
        <span class="font-mono text-5xl font-bold tracking-wider text-amber-400 block mb-1">
          {{ timerDisplay }}
        </span>
        <span class="text-xs text-slate-400 font-mono">
          Mode: {{ timerMode === 'stopwatch' ? 'Elapsed Stopwatch' : `Countdown (${countdownMinutes} minutes)` }}
        </span>
      </div>

      <!-- Mode Selector -->
      <div class="grid grid-cols-2 gap-2 bg-slate-800/80 p-1.5 rounded-xl text-xs font-medium">
        <button 
          @click="setTimerMode('stopwatch')"
          :class="[
            'py-2 rounded-lg text-center transition-colors',
            timerMode === 'stopwatch' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'text-slate-400 hover:text-slate-200'
          ]"
        >
          <i class="pi pi-stopwatch mr-1"></i> Stopwatch (Count Up)
        </button>

        <button 
          @click="setTimerMode('countdown')"
          :class="[
            'py-2 rounded-lg text-center transition-colors',
            timerMode === 'countdown' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'text-slate-400 hover:text-slate-200'
          ]"
        >
          <i class="pi pi-hourglass mr-1"></i> Countdown Mode
        </button>
      </div>

      <!-- Presets for Countdown -->
      <div v-if="timerMode === 'countdown'" class="flex items-center justify-between text-xs">
        <span class="text-slate-400 font-medium">Exam Duration Preset:</span>
        <div class="flex space-x-1.5">
          <button 
            v-for="m in [30, 60, 90, 120]" 
            :key="m"
            @click="setCountdownMinutes(m)"
            :class="[
              'px-2.5 py-1 rounded font-mono text-xs transition-colors',
              countdownMinutes === m ? 'bg-amber-500 text-slate-950 font-bold' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            ]"
          >
            {{ m }}m
          </button>
        </div>
      </div>

      <!-- Controls Buttons -->
      <div class="flex items-center justify-center space-x-3 pt-2">
        <Button 
          @click="toggleTimer" 
          :severity="isRunning ? 'warn' : 'success'"
          class="!px-6 !py-2.5 !text-sm"
        >
          <i :class="isRunning ? 'pi pi-pause mr-2' : 'pi pi-play mr-2'"></i>
          {{ isRunning ? 'Pause Timer' : 'Start Timer' }}
        </Button>

        <Button 
          @click="resetTimer" 
          severity="secondary"
          class="!bg-slate-800 !border-slate-700 !text-slate-300 hover:!bg-slate-700 !px-4 !py-2.5"
        >
          <i class="pi pi-refresh mr-1.5"></i> Reset
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits(['update:visible', 'update-display']);

const visible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val),
});

const timerMode = ref<'stopwatch' | 'countdown'>('stopwatch');
const countdownMinutes = ref(90);
const isRunning = ref(false);
const secondsElapsed = ref(0);
let timerInterval: any = null;

const timerDisplay = computed(() => {
  let sec = 0;
  if (timerMode.value === 'stopwatch') {
    sec = secondsElapsed.value;
  } else {
    sec = Math.max(0, countdownMinutes.value * 60 - secondsElapsed.value);
  }

  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;

  if (h > 0) {
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
});

watch(timerDisplay, (val) => {
  emit('update-display', val);
}, { immediate: true });

function toggleTimer() {
  if (isRunning.value) {
    pauseTimer();
  } else {
    startTimer();
  }
}

function startTimer() {
  if (isRunning.value) return;
  isRunning.value = true;
  timerInterval = setInterval(() => {
    secondsElapsed.value++;
    if (timerMode.value === 'countdown' && secondsElapsed.value >= countdownMinutes.value * 60) {
      pauseTimer();
      alert('⏰ Exam Time is Up!');
    }
  }, 1000);
}

function pauseTimer() {
  isRunning.value = false;
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
}

function resetTimer() {
  pauseTimer();
  secondsElapsed.value = 0;
}

function setTimerMode(mode: 'stopwatch' | 'countdown') {
  resetTimer();
  timerMode.value = mode;
}

function setCountdownMinutes(mins: number) {
  resetTimer();
  countdownMinutes.value = mins;
  timerMode.value = 'countdown';
}

onBeforeUnmount(() => {
  pauseTimer();
});
</script>
